import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
	Dialog,
	DialogContent,
	IconButton,
	Typography,
	Box
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { API_BASE_URL, SSH_WEB_PROXY } from '@config';
import store from 'app/store';
import axios from 'axios';
import { XTerm } from 'xterm-for-react';

const useStyles = makeStyles(theme => ({
	dialog: {
		'& .MuiDialog-paper': {
			backgroundColor: '#0f172a',
			borderRadius: '12px',
			border: '1px solid #334155',
			maxWidth: '900px',
			width: '90%',
			maxHeight: '80vh'
		}
	},
	dialogLight: {
		'& .MuiDialog-paper': {
			backgroundColor: '#ffffff',
			border: '1px solid #e2e8f0'
		}
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: '16px 24px',
		borderBottom: '1px solid #334155'
	},
	headerLight: {
		borderBottom: '1px solid #e2e8f0'
	},
	title: {
		fontSize: '18px',
		fontWeight: 600,
		color: '#f8fafc',
		display: 'flex',
		alignItems: 'center',
		gap: '8px'
	},
	titleLight: {
		color: '#0f172a'
	},
	closeButton: {
		color: '#94a3b8',
		'&:hover': {
			backgroundColor: 'rgba(148, 163, 184, 0.1)'
		}
	},
	closeButtonLight: {
		color: '#64748b',
		'&:hover': {
			backgroundColor: 'rgba(100, 116, 139, 0.1)'
		}
	},
	content: {
		padding: '0 !important',
		backgroundColor: '#0f172a'
	},
	contentLight: {
		backgroundColor: '#ffffff'
	},
	terminalContainer: {
		padding: '16px',
		height: '540px',
		width: '100%',
		overflow: 'hidden',
		backgroundColor: '#0f172a',
		'& .xterm': {
			height: '100% !important',
			width: '100%',
			padding: 0
		},
		'& .xterm-viewport': {
			overflow: 'auto !important'
		}
	},
	terminalContainerLight: {
		backgroundColor: '#ffffff'
	}
}));

const TerminalDialog = ({ open, onClose, themeMode = 'dark' }) => {
	const classes = useStyles();
	const xtermRef = useRef(null);
	const webSocketRef = useRef(null);
	const [displayTerminal, setDisplayTerminal] = useState(false);
	const [loading, setLoading] = useState(true);
	const [connectionError, setConnectionError] = useState(false);
	const isDark = themeMode === 'dark';

	// Initialize terminal when component mounts
	useEffect(() => {
		if (xtermRef.current && displayTerminal && open) {
			// Terminal is automatically opened by xterm-for-react
			const terminal = xtermRef.current.terminal;
			if (terminal) {
				terminal.focus();
			}
		}
	}, [displayTerminal, open]);

	useEffect(() => {
		// Check if terminal is enabled for this user
		setLoading(true);
		axios.get(`${API_BASE_URL}/api/ui/settings`)
			.then(response => {
				setDisplayTerminal(response.data.displayTerminal);
				setLoading(false);
			})
			.catch(error => {
				console.error('Failed to check terminal status:', error);
				setDisplayTerminal(false);
				setLoading(false);
			});
	}, []);

	useEffect(() => {
		if (!open || !displayTerminal) {
			return;
		}

		let isMounted = true;
		setConnectionError(false);

		let purl;
		
		if (SSH_WEB_PROXY) {
			purl = new URL(SSH_WEB_PROXY);
		} else {
			purl = new URL(API_BASE_URL);
			purl.protocol = purl.protocol === 'https:' ? 'wss:' : 'ws:';
			purl.port = '2200';
		}

		if (purl.protocol === 'wss:') {
			webSocketRef.current = new WebSocket(purl, 'ssh', { rejectUnauthorized: false });
		} else {
			webSocketRef.current = new WebSocket(purl);
		}


		// Terminal ref will be set by xterm-for-react when it mounts
		// Set up data handler - will be called once terminal is ready
		setTimeout(() => {
			if (xtermRef.current && xtermRef.current.terminal) {
				xtermRef.current.terminal.onData((data) => {
					if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
						webSocketRef.current.send(data);
					}
				});
			}
		}, 100);

		webSocketRef.current.addEventListener('open', async () => {
			setConnectionError(false);
			const url = `${API_BASE_URL}/api/users/downloadUserSSHKey`;
			const request = await axios.get(url);
			const username = store.getState().auth.user.username;
			webSocketRef.current.send(`${username}:${request.data}`);
		});

		webSocketRef.current.addEventListener('error', (error) => {
			console.error('[TerminalDialog] WebSocket error:', error);
			setConnectionError(true);
		});

		webSocketRef.current.addEventListener('close', () => {
			if (isMounted) {
				setConnectionError(true);
			}
		});

		const handleMessage = (event) => {
			if (isMounted && xtermRef.current && xtermRef.current.terminal) {
				xtermRef.current.terminal.write(event.data);
			}
		};

		webSocketRef.current.addEventListener('message', handleMessage);

		return () => {
			isMounted = false;
			if (webSocketRef.current) {
				webSocketRef.current.removeEventListener('message', handleMessage);
				webSocketRef.current.close();
				webSocketRef.current = null;
			}
		};
	}, [open, displayTerminal]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			className={`${classes.dialog} ${!isDark ? classes.dialogLight : ''}`}
		>
			<Box className={`${classes.header} ${!isDark ? classes.headerLight : ''}`}>
				<Typography className={`${classes.title} ${!isDark ? classes.titleLight : ''}`}>
					<span style={{ fontFamily: 'monospace' }}>&lt;/&gt;</span>
					Terminal
				</Typography>
				<IconButton
					onClick={onClose}
					className={`${classes.closeButton} ${!isDark ? classes.closeButtonLight : ''}`}
					size="small"
				>
					<CloseIcon />
				</IconButton>
			</Box>
			<DialogContent className={`${classes.content} ${!isDark ? classes.contentLight : ''}`}>
				<div className={`${classes.terminalContainer} ${!isDark ? classes.terminalContainerLight : ''}`}>
					{loading ? (
						<Box style={{ 
							padding: '40px', 
							textAlign: 'center',
							color: isDark ? '#94a3b8' : '#64748b'
						}}>
							<Typography variant="body1">
								Loading terminal...
							</Typography>
						</Box>
					) : connectionError ? (
						<Box style={{ 
							padding: '40px', 
							textAlign: 'center',
							color: isDark ? '#94a3b8' : '#64748b'
						}}>
							<Typography variant="h6" style={{ marginBottom: '8px' }}>
								Terminal Unavailable
							</Typography>
							<Typography variant="body2">
								The terminal service is not currently available. Please try again later or contact your administrator.
							</Typography>
						</Box>
					) : displayTerminal ? (
						<div style={{ width: '100%', height: '100%' }}>
							<XTerm 
								ref={xtermRef} 
								options={{ 
									rows: 25, 
									cols: 100,
									cursorBlink: true,
									theme: isDark ? {
									background: '#0f172a',
									foreground: '#f8fafc',
									cursor: '#1C75D2',
									black: '#0f172a',
									red: '#ef4444',
									green: '#10b981',
									yellow: '#f59e0b',
									blue: '#3b82f6',
									magenta: '#a855f7',
									cyan: '#06b6d4',
									white: '#f8fafc',
									brightBlack: '#475569',
									brightRed: '#f87171',
									brightGreen: '#34d399',
									brightYellow: '#fbbf24',
									brightBlue: '#60a5fa',
									brightMagenta: '#c084fc',
									brightCyan: '#22d3ee',
									brightWhite: '#ffffff'
								} : {
									background: '#ffffff',
									foreground: '#0f172a',
									cursor: '#1C75D2',
									black: '#0f172a',
									red: '#dc2626',
									green: '#059669',
									yellow: '#d97706',
									blue: '#2563eb',
									magenta: '#9333ea',
									cyan: '#0891b2',
									white: '#f8fafc',
									brightBlack: '#64748b',
									brightRed: '#ef4444',
									brightGreen: '#10b981',
									brightYellow: '#f59e0b',
									brightBlue: '#3b82f6',
									brightMagenta: '#a855f7',
									brightCyan: '#06b6d4',
									brightWhite: '#ffffff'
								}
							}} 
						/>
						</div>
					) : (
						<Box style={{ 
							padding: '40px', 
							textAlign: 'center',
							color: isDark ? '#94a3b8' : '#64748b'
						}}>
							<Typography variant="h6" style={{ marginBottom: '8px' }}>
								Terminal Access Deactivated
							</Typography>
							<Typography variant="body2">
								Terminal access has been disabled for your account. Please contact your administrator.
							</Typography>
						</Box>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default TerminalDialog;
